/**
 * Abbreviates large numbers with K, M, B suffixes
 * @param num - The number to abbreviate
 * @returns Abbreviated number string (e.g., "1.2K", "3.5M", "105B")
 */
export function formatCompactNumber(num: number): string {
  if (num >= 1_000_000_000) {
    const billions = num / 1_000_000_000;
    return billions >= 10 ? `${Math.floor(billions)}B` : `${billions.toFixed(1)}B`;
  }
  if (num >= 1_000_000) {
    const millions = num / 1_000_000;
    return millions >= 10 ? `${Math.floor(millions)}M` : `${millions.toFixed(1)}M`;
  }
  if (num >= 10_000) {
    const thousands = num / 1_000;
    return thousands >= 10 ? `${Math.floor(thousands)}K` : `${thousands.toFixed(1)}K`;
  }
  return Math.floor(num).toLocaleString();
}

export interface FormatPriceOptions {
  price: number | string;
  currencyCode: string;
  abbreviate?: boolean;
  priceUsd?: number | string;
  roundToFive?: boolean;
  rawPrice?: number | string;
}

export function isPriceOnRequest(rawPrice: number | string | undefined): boolean {
  if (typeof rawPrice !== "string") return false;

  const normalized = rawPrice.trim().toLowerCase().replace(/\s+/g, " ");
  if (!normalized) return false;

  return (
    normalized === "poa" ||
    normalized === "price on request" ||
    normalized === "price upon request" ||
    normalized === "price on application"
  );
}

/**
 * Gets the numeric value that would be displayed by formatPriceWithSymbol
 * Useful for calculations based on the visible price
 */
export function getDisplayedPriceNumeric(options: FormatPriceOptions): number {
  const { price, roundToFive = false, rawPrice, priceUsd } = options;

  if (isPriceOnRequest(rawPrice)) {
    return 0;
  }

  let numPrice = typeof price === "string" ? parseFloat(price) : price;

  // Check for POA (placeholder prices)
  if (priceUsd !== undefined) {
    const numPriceUsd = typeof priceUsd === "string" ? parseFloat(priceUsd) : priceUsd;
    if (numPriceUsd >= 100_000) {
      return 0;
    }
  }

  if (isNaN(numPrice)) {
    return 0;
  }

  // Round to nearest 5 if price was converted
  if (roundToFive && rawPrice !== undefined) {
    const numRawPrice = typeof rawPrice === "string" ? parseFloat(rawPrice) : rawPrice;
    if (numRawPrice !== numPrice) {
      numPrice = Math.round(numPrice / 5) * 5;
    }
  }

  // formatPriceWithSymbol floors the price before displaying
  return Math.floor(numPrice);
}

/**
 * Formats a price with the appropriate currency symbol
 * Detects placeholder prices (>$100K USD equivalent) and returns "POA"
 */
export function formatPriceWithSymbol(options: FormatPriceOptions): string {
  const {
    price,
    currencyCode,
    abbreviate = false,
    priceUsd,
    roundToFive = false,
    rawPrice,
  } = options;

  if (isPriceOnRequest(rawPrice)) {
    return "Price on request";
  }

  let numPrice = typeof price === "string" ? parseFloat(price) : price;

  // Check if this is a placeholder price using USD value
  // $100K USD threshold - fashion items rarely exceed this (even rare vintage/haute couture)
  // This catches placeholder prices like 99,999,999 and unrealistic listings
  if (priceUsd !== undefined) {
    const numPriceUsd = typeof priceUsd === "string" ? parseFloat(priceUsd) : priceUsd;
    if (numPriceUsd >= 100_000) {
      return "POA";
    }
  }

  // handle pro card
  if (isNaN(numPrice)) {
    return price as string;
  }

  // Round to nearest 5 if price was converted (rawPrice differs from price)
  if (roundToFive && rawPrice !== undefined) {
    const numRawPrice = typeof rawPrice === "string" ? parseFloat(rawPrice) : rawPrice;
    if (numRawPrice !== numPrice) {
      numPrice = Math.round(numPrice / 5) * 5;
    }
  }

  const formattedPrice = abbreviate
    ? formatCompactNumber(numPrice)
    : Math.floor(numPrice).toLocaleString();

  if (isNaN(numPrice)) {
    return "";
  }

  switch (currencyCode?.toUpperCase?.()) {
    case "AUD":
    case "USD":
    case "CAD":
    case "NZD":
      return `$${formattedPrice}`;
    case "GBP":
      return `£${formattedPrice}`;
    case "EUR":
      return `${formattedPrice} €`;
    case "DKK":
      return `${formattedPrice} DKK`;
    case "SEK":
      return `${formattedPrice} SEK`;
    case "JPY":
      return `¥${formattedPrice}`;
    case "SGD":
      return `S$${formattedPrice}`;
    case "KRW":
      return `₩${formattedPrice}`;
    case "HKD":
      return `HK$${formattedPrice}`;
    default:
      return `${formattedPrice} ${currencyCode}`;
  }
}
